import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { cn } from "@/lib/utils";

/**
 * GravunCard, the base surface: white, 1px subtle border, restrained radius,
 * no dramatic shadows. All dashboard/detail cards build on this.
 */
export const GravunCard = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "rounded-[10px] border border-border bg-card text-card-foreground",
      className,
    )}
  >
    {children}
  </div>
);

/**
 * SectionCard, a GravunCard with the reference header treatment: small icon,
 * title, muted subtitle, and an optional quiet "action →" link on the right.
 */
export const SectionCard = ({
  icon: Icon,
  title,
  subtitle,
  action,
  actionTo,
  className,
  contentClassName,
  children,
}: {
  icon?: LucideIcon;
  title: string;
  subtitle?: string;
  action?: string;
  actionTo?: string;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
}) => (
  <GravunCard className={className}>
    <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
      <div className="flex items-start gap-2.5">
        {Icon ? (
          <Icon
            className="mt-0.5 size-4 text-muted-foreground"
            strokeWidth={1.75}
          />
        ) : null}
        <div>
          <h2 className="text-[14px] font-semibold leading-5 tracking-tight text-foreground">
            {title}
          </h2>
          {subtitle ? (
            <p className="text-[12px] leading-4 text-muted-foreground">
              {subtitle}
            </p>
          ) : null}
        </div>
      </div>
      {action && actionTo ? (
        <Link
          to={actionTo}
          className="flex shrink-0 items-center gap-0.5 text-[12px] font-medium text-muted-foreground no-underline transition-colors hover:text-foreground"
        >
          {action}
          <ChevronRight className="size-3.5" />
        </Link>
      ) : null}
    </div>
    <div className={cn("px-5 pb-4", contentClassName)}>{children}</div>
  </GravunCard>
);
